/**
 * skyInterpolation — Phase W pure helpers for the dimming-star alpha.
 *
 * GDD §7.5 pairs the `dimmingPct` counter (server-authoritative, 0–100)
 * with a sky star that fades as the percent rises. To stay testable
 * without a Phaser.Scene harness, the interpolation math lives here as a
 * plain function. `OverworldScene` imports it and drives the sprite.
 *
 * Mirrors `portalTransition.ts`'s "pure helpers + unit tests" pattern.
 *
 * Canonical reference: docs/pivot-overnight-plan-4.md §2 Phase W.
 *
 * @packageDocumentation
 */

/**
 * Minimum alpha the star is ever drawn at. At full dimming (100 %) the
 * star keeps a faint presence — the GDD's "years away" framing only
 * makes sense if the player can still see the star while it fades. A
 * pure 0 would render the star invisible long before the Approach
 * arrives and silently break the emotional beat.
 */
export const SKY_STAR_ALPHA_FLOOR = 0.15;

/** Maximum alpha. 1.0 = full opacity. */
export const SKY_STAR_ALPHA_CEIL = 1.0;

/**
 * Map a server-authoritative dimming percentage (0–100) onto the star
 * sprite's alpha channel. The curve is linear per the brief:
 *   - 0 %  → 1.0 (fresh install, bright star)
 *   - 50 % → 0.5
 *   - 85 % → 0.15 (floor kicks in — stays visible)
 *   - 100% → 0.15
 *
 * Non-finite or out-of-range inputs are clamped rather than thrown.
 * Phase W's brief is explicit that the star is always present; a NaN
 * from a malformed broadcast must not hide it.
 */
export function dimmingPctToAlpha(dimmingPct: number): number {
  if (!Number.isFinite(dimmingPct)) return SKY_STAR_ALPHA_CEIL;
  const clamped = Math.max(0, Math.min(100, dimmingPct));
  const raw = 1.0 - clamped / 100;
  if (raw < SKY_STAR_ALPHA_FLOOR) return SKY_STAR_ALPHA_FLOOR;
  if (raw > SKY_STAR_ALPHA_CEIL) return SKY_STAR_ALPHA_CEIL;
  return raw;
}
